import { ChangeDetectorRef, Component, computed, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { IStep, StepsV2Component } from 'shared/components/steps-v2/steps-v2.component';
import {
  ChartComponent,
  ApexNonAxisChartSeries,
  ApexChart,
  ApexPlotOptions,
  ApexDataLabels,
  ApexFill,
  ApexStroke,
  ApexLegend,
  ApexAxisChartSeries,
  ApexXAxis,
  ApexYAxis,
  ApexMarkers,
  ApexGrid,
  ApexTooltip,
  ApexStates
} from 'ng-apexcharts';
import { DatePipe, TitleCasePipe } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MilestoneUserTypeCode } from 'shared/types/user.interface';
import { User } from 'shared/types/user.model';
import { InvitationsService, ISproutStats, UsersService } from 'core';
import { rxResource, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { ICommunityGrowthItem } from 'core/services/users.service';
import { ThousandsSeparatorPipe } from 'shared';
import { NO_PERSISTENT_CHART_SELECTION_EVENTS, NO_PERSISTENT_CHART_SELECTION_STATES } from 'shared/types/chart.config';
import { BalanceService } from 'core/services/balance.service';
import { SortOrders } from 'shared/types/sort-order.enum';
import { formatBalanceMoney } from 'shared/utils/balance-format.utils';
import { ResourceLoadErrorComponent } from 'shared/components/resource-load-error/resource-load-error.component';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  stroke: ApexStroke;
  markers: ApexMarkers;
  yaxis: ApexYAxis;
  grid: ApexGrid;
  dataLabels: ApexDataLabels;
  colors: string[];
  legend: ApexLegend;
  states: ApexStates;
};

export type BrushChartOptions = {
  series: ApexAxisChartSeries;
  chart: any;
  stroke: any;
  fill: any;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  grid: ApexGrid;
  colors: string[];
};

export type DonutChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  colors: string[];
  plotOptions: ApexPlotOptions;
  dataLabels: ApexDataLabels;
  fill: ApexFill;
  stroke: ApexStroke;
  legend: ApexLegend;
  tooltip: ApexTooltip;
  labels: string[];
  states: ApexStates;
};

@Component({
  selector: 'app-journey-tab',
  imports: [
    StepsV2Component,
    ChartComponent,
    TranslatePipe,
    ThousandsSeparatorPipe,
    DatePipe,
    TitleCasePipe,
    ResourceLoadErrorComponent
  ],
  templateUrl: './journey-tab.component.html',
  styleUrl: './journey-tab.component.scss',
  providers: [DatePipe]
})
export class JourneyTabComponent implements OnInit {
  readonly #balanceService = inject(BalanceService);
  invitationsService = inject(InvitationsService);
  usersService = inject(UsersService);
  datePipe = inject(DatePipe);
  translateService = inject(TranslateService);
  destroyRef = inject(DestroyRef);
  changeDetectorRef = inject(ChangeDetectorRef);

  readonly user = input.required<User>();
  readonly isCommissionUser = computed(() => this.user().isSpecial && !this.user().isRewardBased);
  readonly #commissionUser = computed(() => (this.isCommissionUser() ? this.user() : undefined));
  readonly monthlyCommissionResource = rxResource({
    params: () => this.#commissionUser(),
    // Server-side ordering covers the entire history, including months outside the chart's window.
    stream: () =>
      this.#balanceService
        .getPayments({ page: 1, pageSize: 1 }, [], { sortBy: 'amount', sortOrder: SortOrders.DESCEND })
        .pipe(
          map(response => {
            const record = response.results[0];
            return record && Number(record.amount) > 0 ? record : null;
          })
        )
  });
  readonly lifetimeCommissionResource = rxResource({
    params: () => this.#commissionUser(),
    stream: () =>
      this.#balanceService.getBalanceTransactions({ page: 1, pageSize: 1 }).pipe(
        map(response => {
          const total = response.summary?.total_earned;
          if (total == null || !Number.isFinite(Number(total))) throw new Error('Missing commission earnings total');
          return Number(total);
        })
      )
  });
  readonly monthlyCommissionDisplay = computed(() =>
    formatBalanceMoney(
      this.monthlyCommissionResource.hasValue() ? (this.monthlyCommissionResource.value()?.amount ?? 0) : undefined
    )
  );
  readonly lifetimeCommissionDisplay = computed(() =>
    formatBalanceMoney(this.lifetimeCommissionResource.hasValue() ? this.lifetimeCommissionResource.value() : undefined)
  );

  readonly #currentLanguage = toSignal(this.translateService.onLangChange.pipe(map(event => event.lang)), {
    initialValue: this.translateService.currentLang || this.translateService.defaultLang || 'es'
  });
  readonly #communityGrowth = signal<ICommunityGrowthItem[]>([]);
  readonly #communitySeries = computed(() => {
    const dataMap = new Map(this.#communityGrowth().map(item => [item.month, item.community_size]));

    return Array.from({ length: this.months }, (_, index) => {
      const date = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - this.months + 1 + index, 1);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = this.datePipe.transform(date, 'MMM', undefined, this.currentLanguage) || monthKey;

      return { x: monthName.charAt(0).toUpperCase() + monthName.slice(1), y: dataMap.get(monthKey) ?? null };
    });
  });
  readonly #communityAxis = computed(() => {
    const values = this.#communitySeries()
      .map(point => point.y)
      .filter((value): value is number => value !== null && Number.isFinite(value));
    const minValue = values.length ? Math.min(...values) : 0;
    const maxValue = values.length ? Math.max(...values) : 0;
    const padding = Math.max(1, Math.ceil((maxValue - minValue) * 0.1));
    const min = Math.max(0, Math.floor(minValue - padding));
    const step = Math.max(1, Math.ceil((maxValue + padding - min) / 4));

    return { min, max: min + step * 4, step };
  });

  get currentLanguage(): string {
    return this.#currentLanguage().toLowerCase().startsWith('es') ? 'es' : 'en';
  }

  get currentMonthChangeAmount(): number {
    const amount = this.user()?.currentMonthChangeAmount ?? 0;
    return amount < 0 ? 0 : amount;
  }

  public readonly mainChartOptions = computed<ChartOptions>(() => ({
    series: [
      {
        name: this.currentLanguage === 'en' ? 'Community members' : 'Miembros de la comunidad',
        data: this.#communitySeries()
      }
    ],
    chart: {
      type: 'line',
      height: 250,
      toolbar: { show: false },
      zoom: { enabled: false },
      events: NO_PERSISTENT_CHART_SELECTION_EVENTS
    },
    stroke: { curve: 'smooth', width: 2 },
    markers: { size: 3 },
    colors: ['#017F70'],
    dataLabels: { enabled: false },
    grid: { show: false },
    xaxis: {
      type: 'category',
      categories: this.#communitySeries().map(point => point.x),
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        style: {
          colors: '#8A8E94',
          fontSize: '0.9375rem'
        }
      }
    },
    yaxis: {
      min: this.#communityAxis().min,
      max: this.#communityAxis().max,
      tickAmount: 4,
      forceNiceScale: false,
      show: false,
      labels: {
        show: false
      }
    },
    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'center',
      markers: { fillColors: ['#017F70'] }
    },
    states: NO_PERSISTENT_CHART_SELECTION_STATES
  }));

  public brushChart: BrushChartOptions;

  monthlyEarningsRecordDate = computed(() => {
    const month = this.isCommissionUser()
      ? this.monthlyCommissionResource.hasValue()
        ? this.monthlyCommissionResource.value()?.period.slice(0, 7)
        : null
      : this.user().monthlyEarningsRecord?.month;
    return month ? new Date(`${month}-01T12:00:00`) : null;
  });

  acceptedCount = signal<number>(0);
  pendingCount = signal<number>(0);
  totalCount = signal<number>(0);
  donutDataLoaded = signal<boolean>(false);
  mainChartDataLoaded = signal<boolean>(false);

  readonly months: number = 12;
  readonly communityGrowthChange = signal(0);
  readonly communityGrowthMagnitude = computed(() => Math.abs(this.communityGrowthChange()));
  readonly communityGrowthDirection = computed(() => {
    const change = this.communityGrowthChange();
    return change > 0 ? 'up' : change < 0 ? 'down' : 'unchanged';
  });
  readonly communityGrowthLabel = computed(() => {
    switch (this.communityGrowthDirection()) {
      case 'up':
        return this.communityGrowthMagnitude() === 1 ? 'profile.currentMonthUpOne' : 'profile.currentMonthUp';
      case 'down':
        return this.communityGrowthMagnitude() === 1 ? 'profile.currentMonthDownOne' : 'profile.currentMonthDown';
      default:
        return 'profile.currentMonthUnchanged';
    }
  });
  isDonutGrowing = signal(false);
  lastMonthDonut = signal(0);
  readonly yAxisLabels = computed(() => {
    const axis = this.#communityAxis();
    const formatter = new Intl.NumberFormat(this.currentLanguage, { maximumFractionDigits: 0 });

    return Array.from({ length: 5 }, (_, index) => formatter.format(axis.max - index * axis.step));
  });
  readonly isCommunityScaleAdjusted = computed(() => this.#communityAxis().min > 0);

  currentDate = new Date();

  steps = computed<IStep[]>(() => {
    const steps = [
      {
        iconSrc: 'assets/img/my-forest/steps/Seed.svg',
        label: 'my-referrals.seed',
        date: this.user()?.milestoneTimeline?.find(s => s.user_type_code === MilestoneUserTypeCode.SEED)?.change_date
      }
    ];

    if (this.user()?.isSpecial) {
      steps.push(
        {
          iconSrc: 'assets/img/thx-for-application/start-partner.png',
          label: 'buildYourNetwork.signedContract',
          date: this.user()?.milestoneTimeline?.find(s => s.user_type_code === MilestoneUserTypeCode.GANACUATE)
            ?.change_date
        },
        {
          iconSrc: 'assets/img/my-forest/steps/ganasocio.svg',
          label: 'leftSidebar.eligibilitySection.ganasocio',
          date: this.user()?.milestoneTimeline?.find(s => s.user_type_code === MilestoneUserTypeCode.GANASOCIO)
            ?.change_date
        }
      );
    } else {
      steps.push(
        {
          iconSrc: 'assets/img/my-forest/steps/Ganacuate.svg',
          label: 'my-referrals.ganacuate',
          date: this.user()?.milestoneTimeline?.find(s => s.user_type_code === MilestoneUserTypeCode.GANACUATE)
            ?.change_date
        },
        {
          iconSrc: 'assets/img/my-forest/steps/Ganarbol.svg',
          label: 'my-referrals.ganarbol',
          date: this.user()?.milestoneTimeline?.find(s => s.user_type_code === MilestoneUserTypeCode.GANARBOL)
            ?.change_date
        }
      );
    }

    const lastReachedStepIndex = steps.reduce((lastReached, step, index) => (step.date ? index : lastReached), -1);

    return steps.map((step, index) => ({ ...step, active: index <= lastReachedStepIndex }));
  });

  donutChartOptions = computed<DonutChartOptions>(() => ({
    series: [this.acceptedCount(), this.pendingCount()],
    colors: ['#007DCB', '#8AC3E7'],
    chart: {
      type: 'donut',
      height: 250,
      width: 250,
      toolbar: { show: false },
      events: NO_PERSISTENT_CHART_SELECTION_EVENTS
    },
    dataLabels: {
      enabled: false
    },
    legend: {
      show: false
    },
    stroke: {
      width: 0
    },
    plotOptions: {
      pie: {
        donut: {
          size: '60%'
        }
      }
    },
    fill: {
      opacity: 1
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: function (val: number) {
          return val.toString();
        }
      }
    },
    labels: this.currentLanguage === 'en' ? ['Accepted', 'Pending'] : ['Aceptadas', 'Pendientes'],
    states: NO_PERSISTENT_CHART_SELECTION_STATES
  }));

  ngOnInit(): void {
    this.invitationsService
      .getSproutStats()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((stats: ISproutStats) => {
        this.lastMonthDonut.set(stats?.data?.sprouted_seeds?.last_month || 0);
        this.isDonutGrowing.set(stats?.data?.sprouted_seeds?.trend || false);
        this.acceptedCount.set(stats?.data?.sprouted_seeds?.accepted || 0);
        this.pendingCount.set(stats?.data?.sprouted_seeds?.pending || 0);
        this.totalCount.set(stats?.data?.sprouted_seeds?.total || 0);
        this.donutDataLoaded.set(true);

        this.changeDetectorRef.markForCheck();
      });

    this.usersService
      .getCommunityGrowth(this.months)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => {
        this.#communityGrowth.set(response.community_growth ?? []);
        const change = Number(response.current_month_change?.change ?? 0);
        this.communityGrowthChange.set(Number.isFinite(change) ? change : 0);
        this.mainChartDataLoaded.set(true);
        this.changeDetectorRef.markForCheck();
      });
  }
}
